import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from 'src/app/core/common-services/notification.service';
import { UserDataService } from 'src/app/core/data-services/identity/user.data.service';
import { ChangePhoneNumberDto, SendOTPPhoneRequest } from 'src/app/core/data-models/user.data.models';
import { SharedModule } from '../../shared/shared.module';
import { OtpInputComponent } from '../otp-input/otp-input.component';

@Component({
  selector: 'app-edit-phone-number',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './edit-phone-number.component.html',
  styleUrls: ['./edit-phone-number.component.css']
})
export class EditPhoneNumberComponent   {
  @Input() phoneNumber: string = '';
  @Output() updated = new EventEmitter<string>();
  @ViewChild(OtpInputComponent) private otpInput?: OtpInputComponent;

  phoneForm: FormGroup;
  submitted = false;
  step: 'form' | 'otp' = 'form';
  apiError: string = ''; 

  constructor(
    private fb: FormBuilder,
    private translate: TranslateService,
    private userService: UserDataService,
    private notification: NotificationService,
  ) {
    this.phoneForm = this.fb.group(
      {
        currentPhone: ['', [Validators.required, Validators.pattern(/^[0-9]{8,15}$/)]],
        newPhone: ['', [Validators.required, Validators.pattern(/^[0-9]{8,15}$/)]],
        confirmNewPhone: ['', Validators.required],
      },
   { validators: [this.currentPhoneMatches.bind(this), this.newPhonesMatch] }
    );
  }


  get f(): { [key: string]: AbstractControl } {
    return this.phoneForm.controls;
  }

  currentPhoneMatches(form: FormGroup) {
    const currentPhone = form.get('currentPhone')?.value;
    return currentPhone === this.phoneNumber ? null : { currentPhoneMismatch: true };
  }

  /** Validate new phone matches confirm phone */
  newPhonesMatch(form: FormGroup) {
    const newPhone = form.get('newPhone')?.value;
    const confirmPhone = form.get('confirmNewPhone')?.value;
    return newPhone === confirmPhone ? null : { newPhoneMismatch: true };
  }

  onSubmit(): void {
    this.submitted = true;
    this.apiError = '';

    if (this.phoneForm.invalid) {
      this.phoneForm.markAllAsTouched();
      return;
    }

    const request: SendOTPPhoneRequest = { newPhoneNumber: this.phoneForm.value.newPhone };

    this.userService.SendOTPPhone(request).subscribe({
      next: () => {
        this.step = 'otp';
      },
      error: (err) => {
        this.apiError = err?.error?.message || 'Failed to send OTP';
      }
    });
  }

  handleOtpSuccess(otp: string) {
    this.submitted = true;
    this.apiError = '';

    if (this.phoneForm.valid) {
      const request: ChangePhoneNumberDto = {
        newPhoneNumber: this.phoneForm.value.newPhone,
        confirmPhoneNumber: this.phoneForm.value.confirmNewPhone,
        otp
      };

      this.userService.ChangeUserPhoneNumber(request).subscribe({
        next: () => {
          const newPhone = request.newPhoneNumber;
          this.patchStoredUser({ phoneNumber: newPhone });
          this.notification.success('phoneUpdateSuccess');
          this.updated.emit(newPhone);
          this.step = 'form';
          this.phoneForm.reset();
        },
        error: (err) => {
          this.apiError = err?.error?.message || 'Failed to change phone number';
        }
      });
    }
  }

  handleOtpFailed(otp: string) {
    this.apiError = 'OTP verification failed';
  }

  private patchStoredUser(patch: { phoneNumber?: string; email?: string }): void {
    const raw = localStorage.getItem('currentUser');
    if (!raw) return;
    try {
      localStorage.setItem('currentUser', JSON.stringify({ ...JSON.parse(raw), ...patch }));
    } catch {
      // Leave the stored session unchanged if it is not valid JSON.
    }
  }

  /** Resend must hit the same new number the original OTP was stored against. */
  resendPhoneOtp(): void {
    this.apiError = '';
    const request: SendOTPPhoneRequest = { newPhoneNumber: this.phoneForm.value.newPhone };
    this.userService.SendOTPPhone(request).subscribe({
      next: () => this.otpInput?.getOtpForDev(),
      error: (err) => {
        this.apiError = err?.error?.message || 'Failed to send OTP';
      }
    });
  }
}
